import React, {
    Fragment,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import Wrapper from 'ui/Wrapper'
import Loader from 'ui/Loader'
import InfiniteScroll from 'react-infinite-scroller'
import GamesService from 'services/GamesService'
import styled from 'styled-components'
import ResponsiveGrid from 'features/ResponsiveGrid'
import device from 'utils/mediaQueries'
import GridControls from 'features/GridControls'
import UserService from 'services/UserService'
import { BACKGROUNDS, COLORS } from 'themes'
import { useHistory, useRouteMatch } from 'react-router-dom'
import { slideUp } from 'utils/ui'
import PageBanner from 'ui/PageBanner'
import rouletteImage from 'images/backgrounds/roulette_banner.png'
import blackjackImage from 'images/backgrounds/blackjack_banner.jpg'
import pokerImage from 'images/backgrounds/poker_banner.jpg'
import baccaratImage from 'images/backgrounds/baccarat_banner.jpg'

import allIcon from 'images/icons/live-casino/all.png'
import rouletteIcon from 'images/icons/live-casino/roulette.png'
import blackjackIcon from 'images/icons/live-casino/blackjack.png'
import pokerIcon from 'images/icons/live-casino/poker.png'
import baccaratIcon from 'images/icons/live-casino/baccarat.png'
import gameShowIcon from 'images/icons/live-casino/game_show.png'

import getTexts from 'utils/localization'
import { WrapperOverlay } from 'ui/WrapperOverlay'

const CATEGORIES = [
    {
        key: 'all',
        title: 'Alle',
        url: 'all',
        icon: allIcon,
        bgImage: rouletteImage,
    },
    {
        key: 'roulette',
        title: 'Roulette',
        url: 'roulette',
        icon: rouletteIcon,
        bgImage: rouletteImage,
    },
    {
        key: 'baccarat',
        title: 'Baccarat',
        url: 'baccarat',
        icon: baccaratIcon,
        bgImage: baccaratImage,
    },
    {
        key: 'blackjack',
        title: 'Blackjack',
        url: 'blackjack',
        icon: blackjackIcon,
        bgImage: blackjackImage,
    },
    {
        key: 'poker',
        title: 'Poker',
        url: 'poker',
        icon: pokerIcon,
        bgImage: pokerImage,
    },
    {
        key: 'gameShow',
        title: 'Game show',
        url: 'game-show',
        icon: gameShowIcon,
        bgImage: rouletteImage,
    },
]

const FlexWrapper = styled(Wrapper)`
    display: flex;
    overflow: auto;
    flex-direction: row;
    flex-wrap: wrap;
    max-width: 1500px;
    margin: 0 auto;
    width: 100%;
    @media screen and ${device.xxl} {
        max-width: 1000px;
    }
    @media screen and ${device.xl} {
        max-width: 900px;
    }
`

const ButtonListHolder = styled.div`
    display: flex;
    overflow-x: scroll;
    overflow-y: hidden;
    -ms-overflow-style: none;
    scrollbar-width: none;
    margin: 36px 0;

    &::-webkit-scrollbar {
        display: none;
    }
`

const StyledButton = styled.button`
    height: 36px;
    font-size: 16px;
    border: 1px solid ${COLORS.colorBorderPrimaryDefault};
    border-radius: 5px;
    text-transform: none;
    cursor: pointer;
    padding: 0 20px;
    font-weight: bold;
    margin: 0 8px 0 0;
    display: flex;
    align-items: center;
    white-space: nowrap;

    background: ${(props) =>
        props.active
            ? COLORS.colorBackgroundSurfaceOnBrandPrimaryDefault
            : COLORS.colorBackgroundSurfacePrimaryDefault};
    color: ${(props) =>
        props.active
            ? COLORS.colorTextOnBrandPrimaryDefault
            : COLORS.primaryText};
`

const ButtonIcon = styled.img`
    width: 16px;
    height: 16px;
    margin-right: 4px;
`

const LiveCasino = () => {
    const t = getTexts()
    const match = useRouteMatch()
    const history = useHistory()

    const [games, setGames] = useState([])
    const [gamesState, setGamesState] = useState('')
    const [gridType, setGridType] = useState('normal')

    const [pageNumber, setPageNumber] = useState(1)
    const [lastPage, setLastPage] = useState(null)
    const [gamesCount, setGamesCount] = useState(0)
    const [isPageLoading, setIsPageLoading] = useState(false)
    const categoryParam = match.params.category

    const [reachedLastPage, setReachedLastPage] = useState(false)
    const lastAppendedPageRef = useRef(0)

    const activeCategory = useMemo(
        () => CATEGORIES.find((el) => el.url === categoryParam),
        [categoryParam]
    )

    const getIterationLength = () => {
        let type = localStorage.getItem('gridType') || gridType
        return GamesService.getIterationLength(type)
    }

    const changeGridType = (type) => {
        localStorage.setItem('gridType', type)
        setGridType(type)
    }

    const loadFunc = useCallback(
        () => {
            if (isPageLoading) return

            // optionally check if we already loaded all items
            if (games.length >= gamesCount && gamesCount > 0) return

            setIsPageLoading(true)
            setPageNumber((prev) => prev + 1)
        },
        [isPageLoading, games.length, gamesCount]
    )

    const onChangeTabHandler = (button) => {
        history.push(`/live-casino/${button.url}`)
    }

    useEffect(
        () => {
            slideUp()

            if (!activeCategory) {
                history.push('/404')
            }

            //reset pagination on category change
            setPageNumber(1)
            setGames([])
            setGamesCount(0)
            lastAppendedPageRef.current = 0

            const dynamicPageNumber = getIterationLength()
            const category =
                activeCategory.url === 'all' ? '' : activeCategory.url

            GamesService.fetchLiveCasino(category, 1, dynamicPageNumber)
        },
        [activeCategory]
    )

    useEffect(
        () => {
            if (!activeCategory || !activeCategory.url) return
            if (pageNumber <= 1) return

            const dynamicPageNumber = getIterationLength()
            const category =
                activeCategory.url === 'all' ? '' : activeCategory.url

            GamesService.fetchLiveCasino(
                category,
                pageNumber,
                dynamicPageNumber
            )
        },
        [pageNumber]
    )

    useEffect(() => {
        const savedGridType = localStorage.getItem('gridType')
        if (savedGridType) {
            setGridType(savedGridType)
        }
        const gamesSub = GamesService.getLiveCasinoGames().subscribe(
            ({ games: incomingGames, meta }) => {
                if (meta) {
                    setGamesCount(meta.total)
                    setLastPage(meta.last_page || null)
                }

                if (!incomingGames || !incomingGames.length || !meta) {
                    setTimeout(() => setIsPageLoading(false), 500)
                    return
                }

                const currentPage = Number(meta.current_page || 1)
                const last = Number(meta.last_page || 0)

                if (currentPage === 1) {
                    setGames(incomingGames)
                    setReachedLastPage(last > 0 && currentPage >= last)
                    lastAppendedPageRef.current = 1
                } else if (!reachedLastPage) {
                    if (last > 0 && currentPage >= last) {
                        setReachedLastPage(true)
                    }

                    // ignore periodic re-emits of the same page
                    if (currentPage > lastAppendedPageRef.current) {
                        setGames((prev) => [...prev, ...incomingGames])
                        lastAppendedPageRef.current = currentPage
                    }
                }

                setTimeout(() => setIsPageLoading(false), 500)
            }
        )

        const gamesStateSub = GamesService.getGamesState().subscribe((res) => {
            setGamesState(res)
        })
        return () => {
            gamesStateSub.unsubscribe()
            gamesSub.unsubscribe()
        }
    }, [])

    return (
        <WrapperOverlay overlayOffset={250} padding="0 10px 10px">
            {activeCategory && (
                <PageBanner
                    bgImageUrl={activeCategory.bgImage}
                    title={gamesCount ? `${gamesCount} ${t.boards}` : ''}
                />
            )}
            <Wrapper padding="0" width="100%">
                <FlexWrapper>
                    <ButtonListHolder>
                        <Fragment>
                            {CATEGORIES.map((button, key) => {
                                return (
                                    <StyledButton
                                        key={button.key}
                                        onClick={() =>
                                            onChangeTabHandler(button)
                                        }
                                        active={categoryParam === button.url}
                                    >
                                        {/*<ButtonIcon*/}
                                        {/*    src={button.icon}*/}
                                        {/*    alt={button.title}*/}
                                        {/*/>*/}
                                        {button.title}
                                    </StyledButton>
                                )
                            })}
                        </Fragment>
                    </ButtonListHolder>

                    <Fragment>
                        <GridControls
                            category={activeCategory.title}
                            setGridType={changeGridType}
                            gridType={gridType}
                            withSort={false}
                            withFilter={false}
                        />
                        {games.length ? (
                            <InfiniteScroll
                                key={categoryParam}
                                style={{ width: '100%' }}
                                loadMore={loadFunc}
                                pageStart={0}
                                threshold={50}
                                initialLoad={false}
                                hasMore={
                                    !isPageLoading && games.length < gamesCount
                                }
                                loader={
                                    <Loader
                                        key={'loader'}
                                        color={BACKGROUNDS.primary}
                                        margin="20px auto 0"
                                    />
                                }
                            >
                                <ResponsiveGrid
                                    key={'grid'}
                                    onlyForMoney
                                    gridType={gridType}
                                    games={games}
                                />
                            </InfiniteScroll>
                        ) : null}
                    </Fragment>
                </FlexWrapper>

                {gamesState.pending ? <Loader margin="200px auto 0" /> : null}
            </Wrapper>
        </WrapperOverlay>
    )
}

export default LiveCasino
